/**
 * Optional Decorator to cache identical requests and speed up execution
 */
export class CachedAdapter {
  constructor(baseAdapter) {
    this.base = baseAdapter;
    this.cache = new Map();
  }
}

/**
 * Caching proxy to cache method calls for lookups.
 */
export const withCaching = (adapter, ttlMs = 5 * 60 * 1000) => {
  const cache = new Map();
  return new Proxy(adapter, {
    get(target, propKey, receiver) {
      const origMethod = target[propKey];
      if (typeof origMethod === "function") {
        if (typeof propKey === "string" && propKey.startsWith("_")) {
          return origMethod.bind(target);
        }

        return async function (...args) {
          const cacheKey = JSON.stringify({ method: propKey, args });
          const cached = cache.get(cacheKey);
          const now = Date.now();

          if (cached && (now - cached.timestamp < ttlMs)) {
            return cached.data;
          }

          const result = await origMethod.apply(target, args);
          cache.set(cacheKey, { data: result, timestamp: now });
          return result;
        };
      }
      return origMethod;
    }
  });
};


/**
 * Diagnostic proxy to intercept and log all module traffic
 */
export const withLogging = (adapter, moduleName) => {
  return new Proxy(adapter, {
    get(target, propKey, receiver) {
      const origMethod = target[propKey];
      if (typeof origMethod === "function") {
        // Skip logging for internal methods (starting with _) 
        // and keep them synchronous if they were synchronous.
        if (typeof propKey === "string" && propKey.startsWith("_")) {
          return origMethod.bind(target);
        }

        return async function (...args) {
          console.log(`[DataClient] ${moduleName}.${String(propKey)} Start`, { args });
          try {
            // Use target instead of this so internal calls like this._serialize() 
            // aren't intercepted by the proxy again.
            const result = await origMethod.apply(target, args);
            console.log(`[DataClient] ${moduleName}.${String(propKey)} Success`);
            return result;
          } catch (err) {
            console.error(`[DataClient] ${moduleName}.${String(propKey)} Error`, err);
            throw err;
          }
        };
      }
      return origMethod;
    }
  });
};
